import {Card, Chip, Icon, Text} from '@albert-io/atomic';
import {useDroppable} from '@dnd-kit/core';
import cx from 'classnames';
import {useShallow} from 'zustand/react/shallow';
import React, {useCallback, useContext, useEffect, useMemo} from 'react';
import {ItemBankRefsContext} from './ItemBankRefsContext';
import classNames from 'classnames';
import type {QuestionTypeMap} from 'client/components/QuestionTypes/V2/useQuestionStores';
import {MovableItem} from '../../shared/MovableItem.react';
import {
  getSmallSupplement,
  SupplementContent,
  useIsSmallSupplementsEnabled
} from '../../shared/DraggableChip';

interface DroppableItemProps {
  slot: {id: string; label?: string};
  useStoreHook: QuestionTypeMap['item-bank'];
  onClick: () => void;
  onItemClick: (itemId: string, e?: React.MouseEvent) => void;
  onResetItem?: (itemId: string) => void;
  registerValueRenderer?: (slotId: string, el: HTMLDivElement | null) => void;
}

export const DroppableItem = ({
  slot,
  useStoreHook,
  onClick,
  onItemClick,
  onResetItem,
  registerValueRenderer
}: DroppableItemProps) => {
  const {isOver, setNodeRef, node} = useDroppable({id: slot.id});
  const {registerSlotEl} = useContext(ItemBankRefsContext);

  const {activeItemId, isDragging, isSubmitted, correctAnswer, wordBankItems} = useStoreHook(
    useShallow((state) => ({
      dropSlots: state.dropSlots,
      activeItemId: state.activeItemId,
      isDragging: state.isDragging,
      isSubmitted: state.isSubmitted,
      correctAnswer: state.correctAnswer,
      wordBankItems: state.wordBank
    }))
  );

  const placedItem = wordBankItems.find((item) => item.placedInSlot === slot.id);
  const isActiveTarget = activeItemId !== null && !isDragging;
  const handleSlotClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent conflicts with item clicks
    e.stopPropagation();
    onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const isSmallSupplementsEnabled = useIsSmallSupplementsEnabled();
  const smallSupplement =
    isSmallSupplementsEnabled && slot.label ? getSmallSupplement(slot.label) : null;
  const valueRenderer = slot.label ? (
    <SupplementContent
      value={slot.label}
      enabled={isSmallSupplementsEnabled}
      className='rounded-none'
    />
  ) : null;

  useEffect(() => {
    const clickHandler = (e: MouseEvent) => {
      // only handle clicks if an item is active
      if (activeItemId === null) return;
      if (node.current && node.current.contains(e.target as Node)) {
        e.stopPropagation();
        onClick();
      }
    };
    document.addEventListener('click', clickHandler);
    return () => {
      document.removeEventListener('click', clickHandler);
    };
  }, [onClick, node, activeItemId]);

  const isCorrect = useMemo(() => {
    const bankId = placedItem?.id;
    const slotId = slot.id;
    const slotAnswerId = correctAnswer ? correctAnswer[slotId] : null;
    return isSubmitted && slotAnswerId !== null && bankId === slotAnswerId;
  }, [correctAnswer, slot, placedItem, isSubmitted]);

  const correctAnswerForSlot = useMemo(() => {
    const correctAnswerId = correctAnswer ? correctAnswer[slot.id] : null;
    const correctAnswerItem = wordBankItems.find((item) => item.id === correctAnswerId);
    if (!correctAnswerItem) return null;
    return <SupplementContent value={correctAnswerItem.value} enabled={isSmallSupplementsEnabled} />;
  }, [correctAnswer, slot, wordBankItems, isSmallSupplementsEnabled]);

  const valueRendererRef = useCallback(
    (el: HTMLDivElement | null) => registerValueRenderer?.(slot.id, el),
    [registerValueRenderer, slot.id]
  );

  const isPlacing = isOver || isActiveTarget;

  return (
    <Card
      ref={setNodeRef}
      paddingLevel={0}
      className={classNames('h-auto! items-center', {
        'border-2! border-dashed border-selected background-subtle': isOver || isActiveTarget,
        'border-2! border-positive!': isSubmitted && isCorrect,
        'border-2! border-negative!': isSubmitted && !isCorrect
      })}
    >
      <div
        ref={valueRendererRef}
        className={classNames(
          'flex w-full items-center justify-center rounded-t-lg border-b border-border-primary',
          {
            'p-x2 text-center': !smallSupplement,
            'background-subtle': smallSupplement?.type !== 'passage'
          }
        )}
      >
        {valueRenderer}
      </div>
      <div
        data-slot-id={slot.id}
        ref={(el) => registerSlotEl(slot.id, el)}
        onClick={handleSlotClick}
        onKeyDown={handleKeyDown}
        role='button'
        tabIndex={0}
        aria-label={placedItem ? `Slot with ${placedItem.value}` : 'Empty slot'}
        className={cx('flex min-h-x8 w-full items-center justify-center p-x1!', {
          'cursor-pointer background-info-subtle': isPlacing,
          'text-center': !smallSupplement
        })}
      >
        {placedItem && (
          <div
            className={cx('min-w-0 flex max-w-full flex-col justify-center', {
              'text-center': !smallSupplement
            })}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role='presentation'
          >
            <MovableItem
              key={placedItem.id}
              item={placedItem}
              isActive={activeItemId === placedItem.id}
              activeItemId={activeItemId}
              isDragging={isDragging}
              onClick={(e) => {
                if (!isSubmitted) {
                  onItemClick(placedItem.id, e);
                }
              }}
              onReset={onResetItem}
              isCorrect={isCorrect}
              isSubmitted={isSubmitted}
              suppressDimming
            />
            {isSubmitted && !isCorrect && (
              <div className='min-w-0 mt-x1 overflow-hidden rounded-lg border border-border-primary'>
                {correctAnswerForSlot}
              </div>
            )}
          </div>
        )}
        {!placedItem && isSubmitted && !isCorrect && (
          <div className='min-w-0 flex max-w-full flex-col justify-center' role='presentation'>
            <Chip color='negative' size='default' noMargin className='border border-border-primary'>
              <Icon icon={['far', 'circle-xmark']} className='mr-x0-half' />
              No guess
            </Chip>
            <div className='min-w-0 mt-x1 overflow-hidden rounded-lg border border-border-primary'>
              {correctAnswerForSlot}
            </div>
          </div>
        )}
        {!placedItem && !(isSubmitted && !isCorrect) && (
          <Text color={isPlacing ? 'brand' : 'tertiary'}>Place here</Text>
        )}
      </div>
    </Card>
  );
};
